import axios from 'axios';
import { queryOptions } from '@tanstack/react-query';
import { API_ENDPOINTS, PRICING_API } from 'Constants';
import { FerryBookingCreationDto } from 'Models/Dtos/BookingDtos';
import { LinkedBookingPricingResult, TransactionPricingResult } from './PricingResult';
import { buildUrl } from 'Util/FetchUtils';
import { Journey } from 'Global/Journey/Types/Journey';

export interface BasePriceFerryTripsDto {
	bookingCreationDto: FerryBookingCreationDto;
	ferryTripIds: string[];
	journeys: Journey[];
	departureBooking: boolean;
}

/** What one fare class costs on one trip — the cheapest per-head fare in it. */
/** One thing a fare class offers — "Free changes up to 2 hours before departure". */
export interface TripClassAttribute {
	label: string;
	/** Material Symbols name, or blank for a line with no icon. */
	iconName?: string | null;
	order?: number | null;
	showInWizard: boolean;
	showInMoreInfo: boolean;
}

export interface TripClassPrice {
	passengerClassId: string;
	name: string;
	order?: number | null;
	/**
	 * The per-head fare for each active passenger type, keyed by that type's key (A-H).
	 *
	 * <p>Per type rather than one figure: a party's cost in a class depends on who is in it, and two
	 * adults and a child is not three of anything. The card multiplies these by the counts the booker
	 * chose, so a cell is what this booking costs in this class rather than a sample fare.</p>
	 */
	fares: Record<string, number>;
	/**
	 * What the class offers, in the operator's own words. Shown under the sailing once the class is
	 * chosen, which is the one moment the booker is asking what the extra money buys.
	 */
	description?: string | null;
	/**
	 * What the class includes, as lines with icons.
	 *
	 * <p>Both flags are carried because the two surfaces want different subsets: the card lists the
	 * handful promoted to the wizard, and the "more info" popover lists the full set.</p>
	 */
	attributes?: TripClassAttribute[];
	/** The class every passenger starts in on this vessel, unless the booker picks another. */
	isDefault?: boolean;
	/** The vessel's colour for this class, else the class's own. Null falls back to the palette. */
	colour?: string | null;
	/**
	 * Seats still free for a passenger in this class — its own plus unclassed ones. Sent only to
	 * managers and above, who can overbook a class; null for everyone else and for a class with no
	 * seat choice.
	 */
	freeSeats?: number | null;
}

/**
 * Class fares for a whole window of sailings, keyed by trip id.
 *
 * <p>Batched rather than asked per trip: the ticket cards show dozens at once, and it reuses the
 * same bulk pricing the headline "from" price comes from, so a card's class fares and its headline
 * cannot disagree.</p>
 */
export async function FetchTripClassPrices(journeys: Journey[]): Promise<{
	[tripId: string]: TripClassPrice[];
}> {
	if (journeys.length === 0) {
		return {};
	}
	try {
		const result = await axios.post<{ [tripId: string]: TripClassPrice[] }>(
			API_ENDPOINTS.PRICING.GET_TRIP_CLASS_PRICES,
			{ journeys },
		);
		return result.data;
	} catch (e) {
		console.error(e);
		return {};
	}
}

export async function FetchBasePriceForFerryTrips(request: BasePriceFerryTripsDto): Promise<{ [key: string]: number }> {
	try {
		const result = await axios.post<{ [key: string]: number }>(
			API_ENDPOINTS.PRICING.GET_BASE_TICKET_PRICE,
			request,
		);
		return result.data;
	} catch (e) {
		console.error(e);
		return {};
	}
}

/**
 * Prices the wizard's current state for the sidebar.
 *
 * <p>Takes an `AbortSignal` because this runs on every change the booker makes, and the call is the
 * whole of the sidebar's lag — measured 200–850ms, with the summary repainting 7ms after the
 * response. Two changes in quick succession used to leave both requests running: the stale one's
 * result was discarded on arrival, but it had already taken a connection from the browser's six per
 * host and kept the server pricing a booking nobody was looking at any more. The endpoint takes a
 * CancellationToken and passes it down, so aborting here actually stops the work.</p>
 *
 * <p>An abort is not a failure. It returns null like any other miss, and the caller ignores it —
 * see the guard in useFetchBookingPrice, which only lets the newest request set the price.</p>
 */
export async function FetchBookingPricing(
	request: FerryBookingCreationDto | null,
	isAlteration: boolean,
	signal?: AbortSignal,
): Promise<TransactionPricingResult | null> {
	try {
		const result = await axios.post(`${PRICING_API}/get-booking-pricing`, request, { signal });
		const bookingPrices = result.data as LinkedBookingPricingResult[];
		return {
			linkedBookings: bookingPrices,
			forInvoicing: false, // isn't used in this fetch
			isAlteration: isAlteration,
			transactionId: '', // isn't used in this fetch
			total: bookingPrices.reduce((total, booking) => {
				return total + (booking.departureBooking?.totalPrice ?? 0) + (booking.returnBooking?.totalPrice ?? 0);
			}, 0),
		} as TransactionPricingResult;
	} catch (e) {
		// A cancelled request is this function working as intended — the booker changed something
		// else before this answer arrived. Logging it would fill the console with red on every
		// keystroke and bury the failures that matter.
		if (axios.isCancel(e)) {
			return null;
		}
		console.error(e);
		return null;
	}
}

export async function FetchTransactionPricing(
	transactionId: string,
	includeCreditCardFee: boolean,
	forCheckIn: boolean,
): Promise<TransactionPricingResult | null> {
	try {
		const result = await axios.get(
			buildUrl(`${PRICING_API}/get-transaction-pricing`, {
				transactionId,
				includeCreditCardFee: includeCreditCardFee.toString(),
				forCheckIn: forCheckIn.toString(),
			}),
		);
		return result.data as TransactionPricingResult;
	} catch (e) {
		console.error(e);
		return null;
	}
}

/**
 * FetchTransactionPricing as a query, so every reader of one transaction's pricing on a page shares a
 * single request — the booking success page's grand total and its summary, and the cart's prefetch
 * and its summary. Resolves to null on failure, as FetchTransactionPricing does.
 */
export function transactionPricingQueryOptions(
	transactionId: string,
	includeCreditCardFee: boolean,
	forCheckIn: boolean,
) {
	return queryOptions({
		queryKey: ['transaction-pricing', transactionId, includeCreditCardFee, forCheckIn],
		queryFn: () => FetchTransactionPricing(transactionId, includeCreditCardFee, forCheckIn),
	});
}

export async function FetchTrailerCancellationFee(bookingId: string, alterReturn: boolean): Promise<number> {
	try {
		const result = await axios.get(
			buildUrl(`${PRICING_API}/get-trailer-cancellation-fee`, {
				bookingId,
				alterReturn: alterReturn.toString(),
			}),
		);
		return result.data as number;
	} catch (e) {
		console.error(e);
		return 0;
	}
}

interface FreightDetailCalculationRequest {
	userId: string;
	journey: Journey;
	freightTypeId: string;
	freightCategoryId: string;
	quantity: number;
	weight: number;
	width: number;
	height: number;
	length: number;
}

interface FreightDetailCalculationResponse {
	calculation: string;
	totalPrice: number;
}

export async function fetchFreightDetailCalculation(request: FreightDetailCalculationRequest) {
	const result = await axios.post<FreightDetailCalculationResponse>(
		API_ENDPOINTS.PRICING.POST_FREIGHT_DETAIL_CALCULATION,
		request,
	);
	return result.data;
}

interface LunioPricingGraphRequest {
	ferryTripId: string;
	capacity: number;
}

interface LunioPricingGraphResponse {
	points: {
		x: number;
		y: number;
	}[];
	message?: string;
}

export async function fetchLunioPricingGraph(request: LunioPricingGraphRequest) {
	const result = await axios.post<LunioPricingGraphResponse>(API_ENDPOINTS.PRICING.LUNIO_PRICING_GRAPH, request);
	return result.data;
}

export interface BookingPriceBreakdownLine {
	key: string;
	description: string;
	amount: number;
	subText?: string | null;
}

export interface BookingPriceBreakdown {
	lines: BookingPriceBreakdownLine[];
	total: number;
}

/**
 * Super-only: fetches the per-line breakdown of a booking's total, from the same as-paid calculation the
 * detailed-view total uses (so the lines reconcile to the total). Returns null on error or nothing to price.
 */
export async function FetchBookingPriceBreakdown(bookingId: string): Promise<BookingPriceBreakdown | null> {
	try {
		const result = await axios.get(buildUrl(`${PRICING_API}/booking-price-breakdown`, { bookingId }));
		return (result.data as BookingPriceBreakdown) ?? null;
	} catch {
		return null;
	}
}

export interface DiagnosticTicket {
	type: string;
	amount: number | null;
	price: number | null;
	configuration: string | null;
	detail: string | null;
}

export interface DiagnosticFee {
	feeType: string;
	amount: number | null;
	noInterventionAmount: number | null;
	feeOption: string | null;
	/**
	 * True for the one fee class the summary folds into the booking price (an OTHER fee whose option is
	 * refundable). Alteration, cancellation and non-refundable fees are real money but sit outside it.
	 */
	inBookingPrice: boolean;
}

export interface DiagnosticAlteration {
	id: string;
	status: string;
	created: string;
	ferryTripId: string | null;
	tripName: string | null;
	/**
	 * When the alteration's trip departed — the alteration's own start stop's departure on a multi-stop
	 * route, otherwise the trip's. Null on a cancellation, which is blank and holds no trip.
	 */
	tripDeparture: string | null;
	/** "start stop to end stop", falling back to the route's endpoints and then to the route's own name. */
	routeName: string | null;
	isAlteration: boolean;
	tickets: DiagnosticTicket[];
	fees: DiagnosticFee[];
	priceList: Record<string, unknown> | null;
	/**
	 * Product lines for a creation or an edit. On a CANCELLATION (see `isCancellation`) the alteration is
	 * blank, so these are the money the cancellation moved instead — the refund and any credit voucher.
	 */
	lineItems: BookingPriceBreakdownLine[];
	isCancellation: boolean;
	discounts: DiscountDiagnostic[];
}

export interface DiscountDiagnostic {
	label: string;
	/** Negative — what was actually taken off, after the clamp that stops a discount going below zero. */
	amount: number;
	/** The figure as stored. Differs from `amount` when the clamp applied. */
	storedAmount: number;
}

export interface BookingPricingDiagnostic {
	alterations: DiagnosticAlteration[];
}

/**
 * Super-only: the raw per-alteration pricing diagnostic (tickets, fees, configuration, price list, line items).
 */
export async function FetchBookingPricingDiagnostic(bookingId: string): Promise<BookingPricingDiagnostic | null> {
	try {
		const result = await axios.get(buildUrl(`${PRICING_API}/booking-pricing-diagnostic`, { bookingId }));
		return (result.data as BookingPricingDiagnostic) ?? null;
	} catch {
		return null;
	}
}
